import { useCallback, useRef, useState, type KeyboardEvent } from 'react';
import { agentSessionsService, type AgentSessionDTO } from '@/services/roros/agent-sessions.service';

type UseAgentSessionTitleRenameArgs = {
  activeSession: AgentSessionDTO | null;
  onSessionRenamed: (updated: AgentSessionDTO) => void;
};

export function useAgentSessionTitleRename({
  activeSession,
  onSessionRenamed,
}: UseAgentSessionTitleRenameArgs) {
  const [isTitleEditing, setIsTitleEditing] = useState(false);
  const [titleDraft, setTitleDraft] = useState('');
  const [isTitleSaving, setIsTitleSaving] = useState(false);
  const titleInputRef = useRef<HTMLInputElement>(null);

  // AC-CHAT-10: start inline title edit
  const startTitleEdit = useCallback(() => {
    if (!activeSession) return;
    setTitleDraft(activeSession.title ?? '');
    setIsTitleEditing(true);
    setTimeout(() => titleInputRef.current?.select(), 0);
  }, [activeSession]);

  // AC-CHAT-10: save renamed title
  const saveTitleRename = useCallback(async () => {
    if (!activeSession || isTitleSaving) return;
    const trimmed = titleDraft.trim();
    if (!trimmed || trimmed === (activeSession.title ?? '')) {
      setIsTitleEditing(false);
      return;
    }
    setIsTitleSaving(true);
    try {
      const updated = await agentSessionsService.renameSession(activeSession.id, trimmed);
      onSessionRenamed(updated);
    } catch {
      // revert on error
    } finally {
      setIsTitleSaving(false);
      setIsTitleEditing(false);
    }
  }, [activeSession, titleDraft, isTitleSaving, onSessionRenamed]);

  // AC-CHAT-10: keyboard handling for title input
  const handleTitleKeyDown = useCallback((e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') { e.preventDefault(); void saveTitleRename(); }
    if (e.key === 'Escape') { setIsTitleEditing(false); }
  }, [saveTitleRename]);

  return {
    isTitleEditing,
    titleDraft,
    setTitleDraft,
    isTitleSaving,
    titleInputRef,
    startTitleEdit,
    saveTitleRename,
    handleTitleKeyDown,
    cancelTitleEdit: () => setIsTitleEditing(false),
  };
}
